import { FC, RefObject } from "react"

import { ReactComponent as IconWarningCircle } from "@common_assets/svg/warning-circle.svg"
import { Stack, Typography } from "@mui/material"
import IconWrapper from "components/common/icon/iconWrapper"
import { useTranslation } from "react-i18next"

import { Notice } from "../SendStoreChargeDialog.parts"
import { IStoreChargeStudent } from "../SendStoreChargeDialog.types"
import {
  BalanceCheck,
  ICartLine,
  LineStock,
} from "../SendStoreChargeDialog.utils"
import ReviewBalanceCheck from "./ReviewBalanceCheck"
import ReviewItemsTable from "./ReviewItemsTable"
import ReviewStudents from "./ReviewStudents"
import ReviewTotals from "./ReviewTotals"

interface IProps {
  showConflictBanner: boolean
  // The dialog focuses the banner when a 409 lands.
  conflictBannerRef: RefObject<HTMLDivElement>
  studentCount: number
  excludedStudentIds: number[]
  excludedStudents: IStoreChargeStudent[]
  cart: ICartLine[]
  costPerStudent: number
  isRosterFallback: boolean
  isRosterPending: boolean
  isRosterFailed: boolean
  onRetryRoster: () => void
  balanceCheck: BalanceCheck | null
  predictedChargedCount: number
  predictedSkippedCount: number
  noneCanAfford: boolean
  isStockPending: boolean
  lineStocks: LineStock[] | null
  shortLineCount: number
  isStockBlocking: boolean
  // Ids of the messages Charge's aria-describedby can point at.
  balanceMessageId: string
  stockMessageId: string
  rosterErrorId: string
}

const ReviewStep: FC<IProps> = ({
  showConflictBanner,
  conflictBannerRef,
  studentCount,
  excludedStudentIds,
  excludedStudents,
  cart,
  costPerStudent,
  isRosterFallback,
  isRosterPending,
  isRosterFailed,
  onRetryRoster,
  balanceCheck,
  predictedChargedCount,
  predictedSkippedCount,
  noneCanAfford,
  isStockPending,
  lineStocks,
  shortLineCount,
  isStockBlocking,
  balanceMessageId,
  stockMessageId,
  rosterErrorId,
}) => {
  const { t } = useTranslation()

  return (
    <Stack gap={4}>
      {showConflictBanner && (
        <Notice
          ref={conflictBannerRef}
          tabIndex={-1}
          tone="error"
          icon={
            <IconWrapper color="mockup.error50" aria-hidden>
              <IconWarningCircle />
            </IconWrapper>
          }
        >
          <Typography variant="body3" color="mockup.error30">
            {t("storeCharge.conflict.banner")}
          </Typography>
        </Notice>
      )}
      <ReviewStudents
        studentCount={studentCount}
        isRosterFallback={isRosterFallback}
        balanceCheck={balanceCheck}
        excludedStudentIds={excludedStudentIds}
        excludedStudents={excludedStudents}
      />
      <ReviewItemsTable
        cart={cart}
        lineStocks={lineStocks}
        isStockPending={isStockPending}
        isRosterFallback={isRosterFallback}
        shortLineCount={shortLineCount}
        isStockBlocking={isStockBlocking}
        stockMessageId={stockMessageId}
      />
      <ReviewTotals
        costPerStudent={costPerStudent}
        predictedChargedCount={predictedChargedCount}
      />
      <ReviewBalanceCheck
        balanceCheck={balanceCheck}
        isRosterPending={isRosterPending}
        isRosterFailed={isRosterFailed}
        onRetryRoster={onRetryRoster}
        noneCanAfford={noneCanAfford}
        predictedSkippedCount={predictedSkippedCount}
        costPerStudent={costPerStudent}
        balanceMessageId={balanceMessageId}
        rosterErrorId={rosterErrorId}
      />
    </Stack>
  )
}

export default ReviewStep
